export const FILE_SLOT_PREFIX = "file:";
export const FIELD_PREFIX = "field:";
export const BACKEND_DROPPABLE_ID = "BACKEND";

export function normalizeCellValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  if (typeof value === "object") {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part?.text ?? "").join("");
    }
    if (value.result != null) return normalizeCellValue(value.result);
    if (value.text != null) return String(value.text);
    if (value.hyperlink != null) return String(value.hyperlink);
  }
  return String(value);
}

export function toDataGrid(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const width = rows.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0);
  return rows.map((row) => {
    const source = Array.isArray(row) ? row : [];
    const dense = [];
    for (let i = 0; i < width; i += 1) {
      dense.push(normalizeCellValue(source[i]));
    }
    return dense;
  });
}

export function getSpreadsheetColumnName(column) {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let num = column - 1;
  let result = "";
  while (num >= 0) {
    result = letters[num % 26] + result;
    num = Math.floor(num / 26) - 1;
  }
  return result;
}

export function createFileColumnList(names, previousList = []) {
  return (names || []).map((columnName, index) => ({
    id: `${FILE_SLOT_PREFIX}${index}`,
    columnName,
    token: previousList[index]?.token ?? null
  }));
}

export function createBackendColumnList(names) {
  return (names || []).map((columnName) => ({
    id: `${FIELD_PREFIX}${columnName}`,
    columnName
  }));
}

export function columnMatchKey(name) {
  if (typeof name !== "string") return "";
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

export function assignExactColumnMatches(fileList = [], backendNames = []) {
  const remainingByKey = new Map();
  backendNames.forEach((name) => {
    const key = columnMatchKey(name);
    if (key && !remainingByKey.has(key)) remainingByKey.set(key, name);
  });

  const nextFileList = fileList.map((col) => {
    if (col.token) {
      remainingByKey.delete(columnMatchKey(col.token));
      return col;
    }
    const match = remainingByKey.get(columnMatchKey(col.columnName));
    if (!match) return col;
    remainingByKey.delete(columnMatchKey(match));
    return { ...col, token: match };
  });

  return {
    fileList: nextFileList,
    backendNames: backendNames.filter((name) => remainingByKey.has(columnMatchKey(name)))
  };
}

export function getFieldName(id) {
  if (typeof id !== "string" || !id.startsWith(FIELD_PREFIX)) return null;
  return id.slice(FIELD_PREFIX.length);
}

export function getFileSlotIndex(overId, fileList = []) {
  if (typeof overId !== "string") return -1;
  if (overId.startsWith(FILE_SLOT_PREFIX)) {
    const index = Number(overId.slice(FILE_SLOT_PREFIX.length));
    return Number.isInteger(index) ? index : -1;
  }
  if (overId.startsWith(FIELD_PREFIX)) {
    const token = overId.slice(FIELD_PREFIX.length);
    return fileList.findIndex((col) => col.token === token);
  }
  return -1;
}

export function applyColumnDrag({ fileList, backendList, activeId, overId }) {
  if (!overId || activeId === overId) {
    return { fileList, backendList };
  }

  const activeToken = getFieldName(activeId);
  if (!activeToken) {
    return { fileList, backendList };
  }

  const sourceFileIndex = fileList.findIndex((col) => col.token === activeToken);
  const destFileIndex = getFileSlotIndex(overId, fileList);
  const isOverBackend =
    overId === BACKEND_DROPPABLE_ID || backendList.some((item) => item.id === overId || item.columnName === overId);

  if (sourceFileIndex !== -1 && isOverBackend) {
    return {
      fileList: fileList.map((col, index) => (index === sourceFileIndex ? { ...col, token: null } : col)),
      backendList: backendList.some((item) => item.columnName === activeToken)
        ? backendList
        : [...backendList, { id: `${FIELD_PREFIX}${activeToken}`, columnName: activeToken }]
    };
  }

  if (destFileIndex < 0 || destFileIndex >= fileList.length || sourceFileIndex === destFileIndex) {
    return { fileList, backendList };
  }

  const destToken = fileList[destFileIndex].token;

  if (sourceFileIndex === -1) {
    const nextFileList = fileList.map((col, index) => (index === destFileIndex ? { ...col, token: activeToken } : col));
    let nextBackendList = backendList.filter((item) => item.columnName !== activeToken);
    if (destToken && destToken !== activeToken) {
      nextBackendList = [...nextBackendList, { id: `${FIELD_PREFIX}${destToken}`, columnName: destToken }];
    }
    return { fileList: nextFileList, backendList: nextBackendList };
  }

  return {
    fileList: fileList.map((col, index) => {
      if (index === sourceFileIndex) return { ...col, token: destToken };
      if (index === destFileIndex) return { ...col, token: activeToken };
      return col;
    }),
    backendList
  };
}

export function buildInitialColumns(fileColumnNames, backendColumnNames, columnsToSend) {
  const hasSavedFileList = Array.isArray(columnsToSend?.FILE?.list) && columnsToSend.FILE.list.length > 0;
  const fileList = hasSavedFileList
    ? createFileColumnList(
        columnsToSend.FILE.list.map((col) => col.columnName),
        columnsToSend.FILE.list
      )
    : createFileColumnList(fileColumnNames);

  const savedBackendNames = (columnsToSend?.BACKEND?.list || []).map((col) => col.columnName).filter(Boolean);
  const sourceBackendNames = savedBackendNames.length ? savedBackendNames : backendColumnNames || [];
  const matched = hasSavedFileList
    ? { fileList, backendNames: sourceBackendNames }
    : assignExactColumnMatches(fileList, sourceBackendNames);

  const mappedTokens = new Set(matched.fileList.map((col) => col.token).filter(Boolean));
  const backendNames = matched.backendNames.filter((name) => !mappedTokens.has(name));

  return {
    FILE: {
      id: "FILE",
      name: "Imported File",
      list: matched.fileList
    },
    BACKEND: {
      id: BACKEND_DROPPABLE_ID,
      name: "Available Columns",
      list: createBackendColumnList(backendNames)
    }
  };
}

export function mappingListForSubmit(fileList = []) {
  return fileList.map(({ columnName, token }) => ({ columnName, token }));
}
